import React, { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Container, Alert, Button, Modal, Form } from 'react-bootstrap';
import { Helmet } from 'react-helmet';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faFileAlt, faSpinner } from '@fortawesome/free-solid-svg-icons';
import axios from '../utils/api';
import { toast } from 'react-toastify';
import { API_BASE } from '../utils/env';
import FlipbookViewer from '../components/FlipbookViewer';

const PdfViewer = () => {
  const { id, document: docSlug } = useParams();
  const navigate = useNavigate();
  const [pdf, setPdf] = useState(null);
  const [pdfData, setPdfData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [password, setPassword] = useState('');
  const [showPasswordModal, setShowPasswordModal] = useState(false);
  const [downloading, setDownloading] = useState(false);
  const [pageImages, setPageImages] = useState([]);

  const fetchInitiated = useRef(false);

  useEffect(() => {
    return () => {
      setPdfData(prevData => {
        if (prevData && prevData.blobUrl) {
          URL.revokeObjectURL(prevData.blobUrl);
        }
        return null;
      });
    };
  }, []);

  useEffect(() => {
    if (fetchInitiated.current) return;
    fetchInitiated.current = true;

    const fetchPdfAndPages = async () => {
      setLoading(true);
      setError('');
      setPageImages([]);
      setPdfData(null);
      setPdf(null);

      try {
        const url = docSlug ? `/api/pdf/document/${docSlug}` : `/api/pdf/${id}`;
        const response = await axios.get(url);
        const pdfMeta = response.data;
        setPdf(pdfMeta);

        if (pdfMeta.privacy === 'PasswordProtected' && !password) {
          setShowPasswordModal(true);
          setLoading(false);
          return;
        }

        let imagesLoadedFromBackend = false;
        try {
          const manifestResp = await axios.get(`/api/pdf/${pdfMeta.id}/canvas/manifest`);
          if (manifestResp.data && manifestResp.data.pageCount > 0) {
            const version = pdfMeta.sizeBytes || Date.now();
            const images = Array.from({ length: manifestResp.data.pageCount }, (_, i) => 
              `${API_BASE}/api/pdf/${pdfMeta.id}/pages/${i + 1}/canvas?v=${version}`
            );
            setPageImages(images);
            imagesLoadedFromBackend = true;
            console.log("Sucesso: Usando imagens pré-renderizadas do backend.");
          }
        } catch (e) {
          console.warn("Aviso: Manifesto de imagens não encontrado. Usando fallback para renderização no cliente.");
        }

        if (!imagesLoadedFromBackend) {
          console.log("Fallback: Baixando arquivo PDF para renderização no cliente.");
          const version = pdfMeta.sizeBytes || Date.now();
          const fileResponse = await axios.get(`/api/pdf/${pdfMeta.id}/file?v=${version}`, {
            responseType: 'arraybuffer'
          });

          if (fileResponse.data && fileResponse.data.byteLength > 0) {
            const uint8 = new Uint8Array(fileResponse.data);
            setPdfData(uint8);
          } else {
            throw new Error("O arquivo PDF retornado está vazio.");
          }
        }

      } catch (err) {
        const errorMessage = err.response?.status === 404 ? 'PDF não encontrado' : 'Erro ao carregar o PDF';
        setError(errorMessage);
        console.error(errorMessage, err);
      } finally {
        setLoading(false);
      }
    };

    fetchPdfAndPages();

  }, [id, docSlug, password, navigate]);

  const handlePasswordSubmit = async () => {
    if (password === pdf?.password) {
      setShowPasswordModal(false);
      fetchInitiated.current = false; 
    } else {
      toast.error('Senha incorreta');
    }
  };

  const handleDownload = async () => {
    if (!pdf?.allowDownload || downloading) return;

    setDownloading(true);
    const fileName = (pdf.name && pdf.name.toLowerCase().endsWith('.pdf')) ? pdf.name : `${pdf.name || 'documento'}.pdf`;

    try {
      const version = pdf?.sizeBytes || Date.now();
      const resp = await axios.get(`/api/pdf/${pdf.id}/file?v=${version}`, { responseType: 'arraybuffer' });
      
      if (resp.data && resp.data.byteLength > 0) {
        const blob = new Blob([resp.data], { type: 'application/pdf' });
        const blobUrl = URL.createObjectURL(blob);
        
        const link = document.createElement('a');
        link.href = blobUrl;
        link.download = fileName;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        
        URL.revokeObjectURL(blobUrl);
      } else {
        toast.error('Não foi possível baixar o arquivo.');
      }
    } catch (e) {
      toast.error('Falha ao baixar o arquivo.');
    } finally {
      setDownloading(false);
    }
  };

const handleShare = () => {
  if (!pdf?.allowShare) return;

  const url = `${window.location.origin}/viewer/document/${pdf.slug}`;

  if (navigator.clipboard && window.isSecureContext) {
    navigator.clipboard.writeText(url)
      .then(() => toast.success('Link copiado com sucesso!'))
      .catch(() => toast.error('Não foi possível copiar o link'));
  } else {
    // fallback para ambientes sem HTTPS
    const textArea = document.createElement('textarea');
    textArea.value = url;
    document.body.appendChild(textArea);
    textArea.select();
    try {
      document.execCommand('copy');
      toast.success('Link copiado com sucesso!');
    } catch {
      toast.error('Não foi possível copiar o link');
    }
    document.body.removeChild(textArea);
  }
};


  const formatFileSize = (bytes) => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  };

  if (loading) {
    return (
      <div className="min-vh-100 d-flex align-items-center justify-content-center" style={{ backgroundColor: '#ffffff' }}>
        <div className="text-center">
          <div 
            className="d-inline-flex align-items-center justify-content-center mb-4"
            style={{
              width: '80px',
              height: '80px',
              backgroundColor: '#00B2A9',
              borderRadius: '20px'
            }}
          >
            <FontAwesomeIcon icon={faFileAlt} style={{ color: '#ffffff', fontSize: '2rem' }} />
          </div>
          <div className="mb-4">
            <FontAwesomeIcon 
              icon={faSpinner} 
              spin 
              style={{ 
                color: '#00B2A9', 
                fontSize: '3rem',
                animation: 'fa-spin 1s linear infinite'
              }} 
            />
          </div>
          <h3 className="mb-2" style={{ color: '#1e293b' }}>Carregando PDF...</h3>
          <p style={{ color: '#475569' }}>Aguarde enquanto preparamos seu documento</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-vh-100" style={{ backgroundColor: '#f1f5f9' }}>
        <Container className="py-5">
          <div 
            className="p-4 rounded-3 text-center"
            style={{ backgroundColor: '#ffffff', marginTop: '5rem', border: '1px solid #e2e8f0' }}
          >
            <div className="text-danger mb-3" style={{ fontSize: '3rem' }}>⚠️</div>
            <h3 className="mb-2" style={{ color: '#002F6C' }}>Ops! Algo deu errado</h3>
            <p className="mb-4" style={{ color: '#64748b' }}>{error}</p>
            <Button 
              onClick={() => navigate('/gallery')}
              style={{ backgroundColor: '#00B2A9', borderColor: '#00B2A9', borderRadius: '8px' }}
            >
              Voltar para Galeria
            </Button>
          </div>
        </Container>
      </div>
    );
  }

  if (!pdf) {
    return null;
  }

  return (
    <>
      <Helmet>
        <title>{pdf.name} - Gestão de Revistas Digitais</title>
        <meta name="description" content={pdf.description || `Visualize ${pdf.name} em formato de revista digital`} />
      </Helmet>

      <div className="position-relative">
        <FlipbookViewer
          pdfData={pdfData}
          pageImages={pageImages}
          backgroundColor={pdf.backgroundColor || '#ffffff'}
          backgroundImage={pdf.backgroundImage ? `${API_BASE}${pdf.backgroundImage}` : null}
        />
        
{/* Overlay - Hidden on small screens */}
<div className="position-absolute top-0 end-0 p-4 d-none d-lg-block" style={{ zIndex: 1000 }}>
  <div 
    className="rounded-4 shadow-lg"
    style={{ 
      backgroundColor: '#ffffff',
      border: '1px solid rgba(0, 178, 169, 0.2)',
      backdropFilter: 'blur(10px)',
      maxWidth: '320px',
      overflow: 'hidden'
    }}
  >
    {/* Header with gradient */}
    <div 
      className="p-3"
      style={{
        background: 'linear-gradient(135deg, #00B2A9 0%, #002F6C 100%)',
        color: 'white'
      }}
    >
      <h5 className="mb-1 fw-bold">{pdf.name}</h5>
      <div className="d-flex align-items-center justify-content-between">
        <small className="opacity-75">{pdf.pageCount} páginas</small>
        <small className="opacity-75">{formatFileSize(pdf.sizeBytes)}</small>
      </div>
    </div>

    {/* Content */}
    <div className="p-3">
      {pdf.description && (
        <p className="mb-3 small" style={{ color: '#64748b', lineHeight: '1.4' }}>
          {pdf.description}
        </p>
      )}
      
      {/* Action buttons */}
      <div className="d-flex flex-column gap-2">
        <div className="d-flex gap-2">
          {pdf.allowDownload && (
            <Button 
              variant="outline-primary" 
              size="sm" 
              onClick={handleDownload} 
              disabled={downloading}
              className="flex-fill fw-semibold"
              style={{ 
                borderColor: '#00B2A9',
                color: '#00B2A9',
                borderRadius: '8px'
              }}
            >
              {downloading ? 'Baixando...' : '📥 Download'}
            </Button>
          )}
          {pdf.allowShare && (
            <Button 
              variant="outline-primary" 
              size="sm" 
              onClick={handleShare}
              className="flex-fill fw-semibold"
              style={{ 
                borderColor: '#00B2A9',
                color: '#00B2A9',
                borderRadius: '8px'
              }}
            >
              🔗 Compartilhar
            </Button>
          )}
        </div>
        
        {/* Só mostra o botão "Voltar" quando NÃO estiver em /viewer/document/... */}
        {!docSlug && (
          <Button 
            variant="outline-secondary" 
            size="sm" 
            onClick={() => navigate('/gallery')}
            className="fw-semibold"
            style={{ 
              borderRadius: '8px',
              borderColor: 'rgba(100, 116, 139, 0.3)'
            }}
          >
            ← Voltar à Galeria
          </Button>
        )}
      </div>
    </div>
  </div>
</div>


        {/* Mobile floating button - Visible only on small screens */}
        {!docSlug && (
          <div className="position-fixed bottom-0 start-0 end-0 p-3 d-lg-none" style={{ zIndex: 1000 }}>
            <div className="d-flex justify-content-center">
              <div 
                className="d-flex align-items-center gap-2 px-4 py-2 rounded-pill shadow-lg"
                style={{ 
                  backgroundColor: '#ffffff',
                  border: '1px solid rgba(0, 178, 169, 0.2)',
                  backdropFilter: 'blur(10px)'
                }}
              >
                {pdf.allowDownload && (
                  <Button 
                    variant="outline-primary" 
                    size="sm" 
                    onClick={handleDownload} 
                    disabled={downloading}
                    className="fw-semibold"
                    style={{ 
                      borderColor: '#00B2A9',
                      color: '#00B2A9',
                      borderRadius: '20px'
                    }}
                  >
                    {downloading ? 'Baixando...' : '📥'}
                  </Button>
                )}
                {pdf.allowShare && (
                  <Button 
                    variant="outline-primary" 
                    size="sm" 
                    onClick={handleShare}
                    className="fw-semibold"
                    style={{ 
                      borderColor: '#00B2A9',
                      color: '#00B2A9',
                      borderRadius: '20px'
                    }}
                  >
                    🔗
                  </Button>
                )}
                <Button 
                  variant="outline-secondary" 
                  size="sm" 
                  onClick={() => navigate('/gallery')}
                  className="fw-semibold"
                  style={{ 
                    borderRadius: '20px',
                    borderColor: 'rgba(100, 116, 139, 0.3)'
                  }}
                >
                  ←
                </Button>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Password Modal */}
      <Modal 
        show={showPasswordModal} 
        onHide={() => navigate('/gallery')} 
        centered
        contentClassName="border-0"
        style={{ backdropFilter: 'blur(10px)' }}
      >
        <div style={{ backgroundColor: '#ffffff', borderRadius: '15px', overflow: 'hidden', border: '1px solid #e2e8f0' }}>
          <Modal.Header 
            closeButton
            className="border-0 pb-2"
            style={{ backgroundColor: '#ffffff' }}
          >
            <Modal.Title className="d-flex align-items-center" style={{ color: '#002F6C' }}>
              <span className="me-2">🔒</span>
              Senha Necessária
            </Modal.Title>
          </Modal.Header>
          <Modal.Body style={{ backgroundColor: '#ffffff' }}>
            <p style={{ color: '#64748b' }}>
              Este PDF está protegido por senha. Digite a senha para visualizar:
            </p>
            <Form.Control
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              placeholder="Digite a senha"
              onKeyPress={(e) => e.key === 'Enter' && handlePasswordSubmit()}
              style={{
                borderRadius: '8px',
                borderColor: 'rgba(0, 178, 169, 0.3)',
                backgroundColor: '#f8fafc'
              }}
            />
          </Modal.Body>
          <Modal.Footer className="border-0 pt-2" style={{ backgroundColor: '#ffffff' }}>
            <Button 
              variant="outline-secondary" 
              onClick={() => navigate('/gallery')}
              style={{ borderRadius: '8px' }}
            >
              Cancelar
            </Button>
            <Button 
              onClick={handlePasswordSubmit}
              style={{ 
                backgroundColor: '#00B2A9', 
                borderColor: '#00B2A9',
                borderRadius: '8px'
              }}
            >
              Acessar
            </Button>
          </Modal.Footer>
        </div>
      </Modal>
    </>
  );
};

export default PdfViewer;